import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Divider,
  Grid,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Asset } from '../types/asset.types';
import assetService from '../api/assetService';
import { enhancedTaxonomyService } from '../services/enhancedTaxonomyService';
import { formatHFN } from '../utils/hfnFormatter';
import { formatCompositeHfn, formatCompositeMfa } from '../utils/compositeAssetFormatter';
import componentMetadataService, {
  CompositeMetadataAnalysis,
} from '../services/componentMetadataService';
import ComponentChangesBanner from '../components/common/ComponentChangesBanner';
import SongsMetadataEditor from '../components/common/SongsMetadataEditor';
import StarsMetadataEditor from '../components/common/StarsMetadataEditor';
import MovesMetadataEditor, {
  applyIntelligentMovesDefaults,
} from '../components/common/MovesMetadataEditor';
import WorldsMetadataEditor, {
  applyIntelligentWorldsDefaults,
} from '../components/common/WorldsMetadataEditor';
import {
  DEFAULT_STARS_METADATA,
  DEFAULT_SONGS_METADATA,
} from '../types/referencelayerMetadata';
import LooksInformation from '../components/common/LooksInformation';
import ReplaceFileSection from '../components/common/ReplaceFileSection';
import aiContractService from '../services/aiContractService';
import { safeIncludes } from '../utils/arrayStringHelpers';
import { AlgorhythmMetadataDisplay } from '../components/algorhythm/AlgorhythmMetadataDisplay';
import { AggregatedMetadataDisplay } from '../components/algorhythm/AggregatedMetadataDisplay';
import { AlgorhythmMetadata, AggregatedMetadata } from '../types/algorhythm.types';
import { ComponentMetadataSections } from '../components/composite/ComponentMetadataSections';
import { generateVersionNotes } from '../utils/versionNotesGenerator';
import { isAlgorhythmRequestComplete, isLayerRequiredFieldsComplete } from '../constants/apiMetadataConstraints';
import { buildLayerAssetUpdateRequest } from './assetEditUpdatePayload';

const LAYER_LABELS: Record<string, string> = {
  G: 'Song',
  S: 'Star',
  L: 'Look',
  M: 'Move',
  W: 'World',
  C: 'Composite',
  B: 'Brand',
  P: 'Personalize',
  T: 'Training',
  R: 'Rights',
};

// Component to handle Base Song ID display with conversion
const BaseSongIdDisplay: React.FC<{ baseSongId: string }> = ({
  baseSongId,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(baseSongId || 'N/A');
  const [isConverting, setIsConverting] = useState<boolean>(false);

  React.useEffect(() => {
    const convertIfNeeded = async () => {
      if (!baseSongId) {
        setDisplayValue('N/A');
        return;
      }

      // Check if it's a MongoDB ObjectID (24 character hex string)
      const isMongoId = /^[0-9a-fA-F]{24}$/.test(baseSongId);

      if (isMongoId) {
        setIsConverting(true);
        console.log(
          '🔄 [EDIT DETAILS] Converting MongoDB ID to HFN (Songs):',
          baseSongId
        );

        try {
          const response = await assetService.getAssetById(baseSongId);
          if (response) {
            setDisplayValue(response.name || 'Unknown');
            console.log(
              '✅ [EDIT DETAILS] Converted Song MongoDB ID to HFN:',
              response.name
            );
          } else {
            setDisplayValue('Unknown Asset');
          }
        } catch (error) {
          console.error(
            '❌ [EDIT DETAILS] Failed to convert Song MongoDB ID:',
            error
          );
          setDisplayValue('Unknown Asset');
        } finally {
          setIsConverting(false);
        }
      } else {
        setDisplayValue(baseSongId);
      }
    };

    convertIfNeeded();
  }, [baseSongId]);

  if (isConverting) {
    return <Typography variant="body2">Converting...</Typography>;
  }

  return (
    <Typography variant="body2" fontWeight="medium">
      {displayValue}
    </Typography>
  );
};

/**
 * CRITICAL FIX: Get complete AlgoRhythm metadata by merging from multiple sources
 *
 * BACKEND BUG WORKAROUND: Due to a backend bug (Bug #27 - November 27, 2025),
 * root-level `algorhythmMetadata` is incomplete - it only has 4 of 7 fields:
 * - ✅ genre, mood, targetAudience, ageAppropriateness (present)
 * - ❌ culturalContext, performanceContext, musicalStyle (MISSING)
 *
 * These missing fields ARE present in nested structures:
 * - aiMetadata.layerMetadata.algorhythmMetadata (complete - 7 fields)
 * - songMetadata.algorhythmMetadata (complete - 7 fields)
 *
 * This function merges data from all sources to ensure complete display.
 *
 * @param asset - The asset object
 * @returns Complete AlgoRhythm metadata with all 7 fields
 */
const getCompleteAlgorhythmMetadata = (asset: any): AlgorhythmMetadata | null => {
  if (!asset) return null;

  // Start with root-level metadata (incomplete but has some fields)
  const merged: any = { ...(asset.algorhythmMetadata || {}) };

  // Try to get complete metadata from nested sources (priority order)
  const sources = [
    asset.songMetadata?.algorhythmMetadata,           // Songs layer: songMetadata.algorhythmMetadata
    asset.aiMetadata?.layerMetadata?.algorhythmMetadata, // AI metadata: layerMetadata.algorhythmMetadata
    asset.aiMetadata?.layerMetadata,                  // Fallback: layerMetadata itself
  ];

  // Merge fields from sources (first non-empty value wins)
  const fieldNames = [
    'genre',
    'mood',
    'targetAudience',
    'ageAppropriateness',
    'culturalContext',      // Missing from root level (backend bug)
    'musicalStyle',         // Missing from root level (backend bug)
    'performanceContext',   // Missing from root level (backend bug)
    'energyLevel',
  ];

  for (const source of sources) {
    if (!source) continue;

    for (const field of fieldNames) {
      // Only copy if we don't already have this field
      if (!merged[field] || (Array.isArray(merged[field]) && merged[field].length === 0)) {
        const value = source[field];
        if (value && (!Array.isArray(value) || value.length > 0)) {
          merged[field] = value;
          console.log(`🔧 [ALGORHYTHM MERGE] Copied ${field} from nested source:`, value);
        }
      }
    }
  }

  // CRITICAL FIX: Special handling for energyLevel extraction and normalization
  // Backend stores as "energyLevel" in algorhythmMetadata but "energy" in songMetadata/layerMetadata
  // Priority: root algorhythmMetadata → songMetadata.energy → layerMetadata.energy/energyLevel
  if (!merged.energyLevel) {
    const energyValue =
      asset.songMetadata?.energy ||
      asset.aiMetadata?.layerMetadata?.energy ||
      asset.aiMetadata?.layerMetadata?.energyLevel;

    if (energyValue) {
      // Normalize to valid values: 'low' | 'medium' | 'high' | 'extreme'
      const normalized = String(energyValue).toLowerCase().trim();
      const validValues = ['low', 'medium', 'high', 'extreme'];

      if (validValues.includes(normalized)) {
        merged.energyLevel = normalized;
      } else if (normalized.includes('high') || normalized.includes('extreme')) {
        merged.energyLevel = 'high';
      } else if (normalized.includes('low')) {
        merged.energyLevel = 'low';
      } else if (normalized.includes('medium') || normalized.includes('moderate')) {
        merged.energyLevel = 'medium';
      } else {
        // Default fallback
        merged.energyLevel = 'medium';
      }

      console.log(`🔧 [ALGORHYTHM MERGE] Copied and normalized energyLevel:`, {
        original: energyValue,
        normalized: merged.energyLevel
      });
    }
  }

  console.log('✅ [ALGORHYTHM MERGE] Complete metadata:', merged);
  return merged as AlgorhythmMetadata;
};

const AssetEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Ref to store corrected songMetadata for immediate access during render
  const songMetadataRef = useRef<any>(null);

  const [asset, setAsset] = useState<Asset | null>(null);
  const [creatorDescription, setCreatorDescription] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);
  const [albumArtUrl, setAlbumArtUrl] = useState('');
  const [, setOriginalAlbumArtUrl] = useState('');
  // Phase 2C: Song metadata state variables
  const [songName, setSongName] = useState('');
  const [artistName, setArtistName] = useState('');
  const [albumName, setAlbumName] = useState('');
  const [bpm, setBpm] = useState<number | ''>('');
  const [genre, setGenre] = useState('');
  const [releaseYear, setReleaseYear] = useState<number | ''>('');
  const [duration, setDuration] = useState<number | ''>('');
  const [remixedBy, setRemixedBy] = useState('');
  const [originalSongId, setOriginalSongId] = useState('');
  // NEW: Gender information state variables
  const [originalArtistGender, setOriginalArtistGender] = useState<
    'male' | 'female' | 'group' | 'Non-binary' | 'unknown' | ''
  >('');
  const [genderSuitability, setGenderSuitability] = useState<
    'male' | 'female' | 'unisex' | ''
  >('');
  const [vocalRange, setVocalRange] = useState<'low' | 'medium' | 'high' | ''>(
    ''
  );
  const [ageAppropriateness, setAgeAppropriateness] = useState<string[]>([]);
  // NEW: Star metadata state variables
  const [starMetadata, setStarMetadata] = useState<any>(null);
  // NEW: Consolidated song metadata state for SongsMetadataEditor
  const [songMetadata, setSongMetadata] = useState<any>(null);
  // NEW: Looks metadata state variables
  const [brandName, setBrandName] = useState('');
  const [styleCategory, setStyleCategory] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [colorScheme, setColorScheme] = useState('');
  const [primaryGarment, setPrimaryGarment] = useState('');
  const [seasonality, setSeasonality] = useState('');
  const [occasion, setOccasion] = useState('');
  const [trendLevel, setTrendLevel] = useState('');
  const [fit, setFit] = useState('');
  const [material, setMaterial] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [accessories, setAccessories] = useState<string[]>([]);
  const [looksMetadataState, setLooksMetadataState] = useState<any>(null);
  const [movesMetadata, setMovesMetadata] = useState<any>(null);
  const [worldsMetadata, setWorldsMetadata] = useState<any>(null);
  const [formattedHFN, setFormattedHFN] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Component metadata state for composite assets
  const [metadataAnalysis, setMetadataAnalysis] =
    useState<CompositeMetadataAnalysis | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [isComposite, setIsComposite] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);

  // Effective Algorhythm payload for G layer (7 API-required fields) for Save button disable check
  const effectiveAlgorhythmPayload = React.useMemo(() => {
    if (!asset || asset.layer !== 'G' || !songMetadata) return null;
    const toArr = (v: unknown): string[] => {
      if (Array.isArray(v)) return v.length ? v.map(String) : [];
      if (typeof v === 'string' && v.trim() !== '') return [v.trim()];
      return [];
    };
    return {
      genre: toArr(songMetadata.genre),
      mood: toArr(songMetadata.mood),
      targetAudience: toArr(songMetadata.targetAudience),
      ageAppropriateness: toArr(songMetadata.ageAppropriateness),
      culturalContext: toArr(songMetadata.culturalContext),
      musicalStyle: toArr(songMetadata.musicalStyle),
      performanceContext: toArr(songMetadata.performanceContext),
    };
  }, [asset, songMetadata]);

  // For G layer always require 7 Algorhythm fields before Save (form always shows them). Other layers could use shouldShowAlgorhythmFields if we add Algorhythm sections later.
  const algorhythmIncompleteAndShown =
    !!asset &&
    asset.layer === 'G' &&
    !isAlgorhythmRequestComplete(effectiveAlgorhythmPayload ?? undefined);

  // Effective layer metadata payload for Save button (OpenAPI required fields per layer)
  const effectiveLayerPayload = React.useMemo((): Record<string, unknown> | null => {
    if (!asset) return null;
    const layer = asset.layer;
    if (layer === 'G') {
      if (!songMetadata) return null;
      return {
        genre: songMetadata.genre,
        mood: songMetadata.mood,
        songName: songMetadata.songName,
        artistName: songMetadata.artistName,
        tempo: songMetadata.tempo,
      } as Record<string, unknown>;
    }
    if (layer === 'S') return starMetadata as Record<string, unknown> | null;
    if (layer === 'L') {
      const looks = looksMetadataState || {};
      return {
        outfitName: looks.outfitName ?? '',
        styleCategory: styleCategory ?? looks.styleCategory ?? '',
        style: looks.style ?? '',
        occasion: occasion ?? looks.occasion ?? '',
        colorScheme: colorScheme ?? looks.colorScheme ?? '',
        primaryGarment: primaryGarment ?? looks.primaryGarment ?? '',
        primaryColors: Array.isArray(looks.primaryColors) && looks.primaryColors.length > 0 ? looks.primaryColors : (primaryColor ? [primaryColor] : []),
        formality: looks.formality ?? '',
      };
    }
    if (layer === 'M') return movesMetadata as Record<string, unknown> | null;
    if (layer === 'W') return worldsMetadata as Record<string, unknown> | null;
    return null;
  }, [asset, songMetadata, starMetadata, looksMetadataState, movesMetadata, worldsMetadata, styleCategory, primaryColor, colorScheme, primaryGarment, occasion]);

  const layerRequiredComplete = React.useMemo(() => {
    if (!asset) return true;
    if (!['G', 'S', 'L', 'M', 'W'].includes(asset.layer)) return true;
    if (effectiveLayerPayload == null) return false;
    return isLayerRequiredFieldsComplete(asset.layer, effectiveLayerPayload);
  }, [asset, effectiveLayerPayload]);

  // Debug logging for asset loading
  useEffect(() => {
    if (asset) {
      console.log('🔍 [EDIT DETAILS] Asset loaded:', {
        id: asset._id || asset.id,
        layer: asset.layer,
        hasAiMetadata: !!asset.aiMetadata,
        hasStarsMetadata: !!asset.aiMetadata?.starsMetadata,
        hasStarMetadata: !!asset.starMetadata,
        aiMetadataKeys: asset.aiMetadata ? Object.keys(asset.aiMetadata) : [],
        starMetadataKeys: asset.starMetadata
          ? Object.keys(asset.starMetadata)
          : [],
        rawAiMetadata: asset.aiMetadata,
        rawStarMetadata: asset.starMetadata,
      });
    }
  }, [asset]);

  // Get taxonomy path for breadcrumb display using Backend API
  const [taxonomyPath, setTaxonomyPath] = useState<string>('');

  // Load taxonomy path when asset is loaded
  useEffect(() => {
    const loadTaxonomyPath = async () => {
      if (!asset) return;

      try {
        const path = await enhancedTaxonomyService.getTaxonomyPath(
          asset.layer,
          asset.category || asset.categoryCode,
          asset.subcategory || asset.subcategoryCode
        );
        setTaxonomyPath(path);
      } catch (error) {
        console.warn('Failed to load taxonomy path, using fallback:', error);

        // Fallback to simple mapping if Backend API fails
        const layerNames: Record<string, string> = {
          G: 'Songs',
          S: 'Stars',
          L: 'Looks',
          M: 'Moves',
          W: 'Worlds',
          C: 'Composites',
          B: 'Branded',
          P: 'Personalize',
          T: 'Training Data',
          R: 'Rights',
        };

        const layer = asset.layer;
        const category = asset.category || asset.categoryCode;
        const subcategory = asset.subcategory || asset.subcategoryCode;

        const layerName = layerNames[layer] || layer;
        setTaxonomyPath(`${layerName} > ${category} > ${subcategory}`);
      }
    };

    loadTaxonomyPath();
  }, [asset]);

  // Get MFA (same logic as AssetDetailPage)
  const getMFA = () => {
    if (!asset) return '';
    return (
      (asset as any).nna_address ||
      asset.nnaAddress ||
      asset.metadata?.machineFriendlyAddress ||
      asset.metadata?.mfa ||
      ''
    );
  };

  useEffect(() => {
    const loadAsset = async () => {
      if (!id) {
        setError('Asset ID is required');
        setLoading(false);
        return;
      }

      const LAYERS_NEW_UPDATE = ['G', 'S', 'L', 'M', 'W'];

      try {
        setLoading(true);
        setError(null);

        // For G,S,L,M,W use getAssetDetails + get*Metadata + getAssetTags (aligned with PUT *AssetUpdateRequest)
        try {
          const details = await assetService.getAssetDetails(id);
          if (LAYERS_NEW_UPDATE.includes(details.layer)) {
            const [tagsRes, layerMeta] = await Promise.all([
              assetService.getAssetTags(id),
              details.layer === 'G' ? assetService.getSongMetadata(id) :
              details.layer === 'S' ? assetService.getStarMetadata(id) :
              details.layer === 'L' ? assetService.getLookMetadata(id) :
              details.layer === 'M' ? assetService.getMoveMetadata(id) :
              assetService.getWorldMetadata(id),
            ]);
            const tags = tagsRes?.tags ?? [];
            const syntheticAsset: Asset = {
              id: details.id,
              _id: details.id,
              name: details.humanFriendlyName ?? '',
              friendlyName: details.humanFriendlyName ?? '',
              machineFriendlyName: (details as { machineFriendlyAddress?: string }).machineFriendlyAddress ?? '',
              layer: details.layer,
              categoryCode: details.categoryCode ?? details.categoryName ?? '',
              subcategoryCode:
                details.subcategoryCode ?? details.subcategoryName ?? '',
              type: 'base',
              imageUrl: details.fileUrl ?? '',
              files: [],
              metadata: { fileUrl: details.fileUrl, videoUrl: details.fileUrl },
              createdAt: '',
              updatedAt: '',
              createdBy: '',
              status: 'active',
              description: details.description ?? '',
              tags,
              ...(details.layer === 'G' && { songMetadata: layerMeta }),
              ...(details.layer === 'S' && { starMetadata: layerMeta }),
              ...(details.layer === 'L' && { looksMetadata: layerMeta }),
              ...(details.layer === 'M' && { movesMetadata: layerMeta }),
              ...(details.layer === 'W' && { worldsMetadata: layerMeta }),
            } as Asset;
            setAsset(syntheticAsset);
            setDescription(details.description ?? '');
            setTags(tags);
            setCreatorDescription((layerMeta as any)?.creatorDescription ?? '');
            try {
              const properHFN = await formatHFN(syntheticAsset);
              setFormattedHFN(properHFN);
            } catch {
              setFormattedHFN(details.humanFriendlyName ?? 'Unknown');
            }
            if (details.layer === 'G') setSongMetadata(layerMeta as any);
            else if (details.layer === 'S') setStarMetadata(layerMeta as any);
            else if (details.layer === 'M') setMovesMetadata(layerMeta as any);
            else if (details.layer === 'W') setWorldsMetadata(layerMeta as any);
            else if (details.layer === 'L') {
              setLooksMetadataState(layerMeta as any);
              const lm = layerMeta as any;
              setBrandName(Array.isArray(lm.brandNames) ? lm.brandNames[0] ?? '' : lm.brandNames ?? '');
              setStyleCategory(lm.styleCategory ?? '');
              setPrimaryColor(Array.isArray(lm.primaryColors) ? lm.primaryColors[0] ?? '' : lm.primaryColor ?? '');
              setColorScheme(lm.colorScheme ?? '');
              setPrimaryGarment(lm.primaryGarment ?? '');
              setSeasonality(lm.seasonality ?? '');
              setOccasion(lm.occasion ?? '');
              setPriceRange(lm.priceRange ?? '');
              setAccessories(Array.isArray(lm.accessories) ? lm.accessories : []);
            }
            setLoading(false);
            return;
          }
        } catch (_) {
          // Fall through to legacy getAssetById
        }

        const assetData = await assetService.getAssetById(id);
        setAsset(assetData);

        // Format the HFN using Backend Taxonomy API
        try {
          const properHFN = await formatHFN(assetData);
          setFormattedHFN(properHFN);
        } catch (hfnError) {
          console.warn('Failed to format HFN, using fallback:', hfnError);
          setFormattedHFN(assetData.name || 'Unknown');
        }

        // CRITICAL FIX: Consistent field mapping across all components
        // CreatorDescription: For G layer, read from songMetadata.creatorDescription
        // For other layers, read from root asset.creatorDescription
        const creatorDesc = assetData.layer === 'G'
          ? (assetData as any).songMetadata?.creatorDescription || ''
          : assetData.creatorDescription || '';
        setCreatorDescription(creatorDesc);
        console.log('📝 [CREATOR DESC] Layer:', assetData.layer, 'Value:', creatorDesc, 'Source:', assetData.layer === 'G' ? 'songMetadata' : 'root');

        // AI-Generated Description: Use dedicated AI sources (NEVER use main description field - that's creatorDescription!)
        // LAYER-AGNOSTIC: Check layerMetadata.description first (works for all layers including Worlds)
        // CRITICAL FIX: For Songs layer, prioritize songMetadata.description per backend team guidance
        const assetAny = assetData as any;
        const descriptionSources = {
          layerMetadataDescription: assetData.aiMetadata?.layerMetadata?.description,
          aiMetadataDescription: assetData.aiMetadata?.description,
          aiMetadataGenerated: assetData.aiMetadata?.generatedDescription,
          aiDescription: assetAny.aiDescription,
          songMetadataDescription: assetAny.songMetadata?.description,
        };

        console.log('🔍 [EDIT PAGE] AI Description sources analysis:', {
          layer: assetData.layer,
          sources: descriptionSources,
          priorityForSongs: assetData.layer === 'G' ? 'songMetadata.description' : 'layerMetadata.description'
        });

        // BACKEND TEAM FIX: For Songs layer (G), prioritize songMetadata.description
        let finalDescription = '';
        if (assetData.layer === 'G' && assetAny.songMetadata?.description) {
          finalDescription = assetAny.songMetadata.description;
          console.log('🎵 [EDIT PAGE] Using songMetadata.description for Songs layer asset');
        } else {
          // CRITICAL FIX: Check top-level description field FIRST (where backend stores it)
          // Then fallback to nested locations for backward compatibility
          finalDescription =
            assetData.description ||  // ✅ PRIMARY: Backend stores AI description here
            assetData.aiMetadata?.layerMetadata?.description ||
            assetData.aiMetadata?.description ||
            assetData.aiMetadata?.generatedDescription ||
            assetAny.aiDescription ||
            assetAny.songMetadata?.description ||
            '';
        }

        console.log('🔍 [EDIT PAGE] Final AI description selected:', {
          source: finalDescription ? 'Found' : 'None',
          length: finalDescription?.length || 0,
          preview: finalDescription?.substring(0, 100) + '...',
          layer: assetData.layer,
          prioritizedSongMetadata: assetData.layer === 'G' && assetAny.songMetadata?.description ? true : false
        });

        setDescription(finalDescription);
        setTags(assetData.tags || []);
        // Initialize album art URL for Songs layer assets
        const initialAlbumArtUrl =
          assetData.albumArt || assetData.metadata?.albumArtUrl || '';
        setAlbumArtUrl(initialAlbumArtUrl);
        setOriginalAlbumArtUrl(initialAlbumArtUrl);

        // Phase 2C: Initialize song metadata for Songs and Composite layer assets
        if (
          (assetData.layer === 'G' || assetData.layer === 'C') &&
          assetData.songMetadata
        ) {
          console.log(
            '[EDIT PAGE] assetData.songMetadata raw:',
            assetData.songMetadata
          );

          try {
            const songData =
              typeof assetData.songMetadata === 'string'
                ? JSON.parse(assetData.songMetadata)
                : assetData.songMetadata;

            // Debug song metadata parsing in development only
            if (process.env.NODE_ENV === 'development') {
              console.log('[EDIT PAGE] Song data loaded:', {
                songName: songData.songName,
                artistName: songData.artistName,
              });
            }

            setSongName(songData.songName || '');
            setArtistName(songData.artistName || '');
            setAlbumName(songData.albumName || '');
            setBpm(songData.bpm || '');
            setGenre(songData.genre || '');
            setReleaseYear(songData.releaseYear || '');
            setDuration(songData.duration || '');
            setRemixedBy(songData.remixedBy || '');
            setOriginalSongId(songData.originalSongId || '');

            // NEW: Initialize gender information fields
            setOriginalArtistGender(songData.originalArtistGender || '');
            setGenderSuitability(songData.genderSuitability || '');
            setVocalRange(songData.vocalRange || '');
            // CRITICAL FIX: Handle ageAppropriateness as string from backend
            if (songData.ageAppropriateness) {
              if (typeof songData.ageAppropriateness === 'string') {
                // Backend returns string like "all-ages" - convert to frontend format
                const frontendValues = [];
                if (
                  safeIncludes(songData.ageAppropriateness, 'all') ||
                  safeIncludes(songData.ageAppropriateness, 'everyone')
                ) {
                  frontendValues.push('all ages');
                }
                if (safeIncludes(songData.ageAppropriateness, 'teen')) {
                  frontendValues.push('teen+');
                }
                if (
                  safeIncludes(songData.ageAppropriateness, 'adult') &&
                  !safeIncludes(songData.ageAppropriateness, 'young')
                ) {
                  frontendValues.push('adult');
                }
                if (safeIncludes(songData.ageAppropriateness, 'young adult')) {
                  frontendValues.push('young adult');
                }
                setAgeAppropriateness(
                  frontendValues.length > 0 ? frontendValues : ['all ages']
                );
              } else if (Array.isArray(songData.ageAppropriateness)) {
                setAgeAppropriateness(songData.ageAppropriateness);
              } else {
                setAgeAppropriateness([]);
              }
            } else {
              setAgeAppropriateness([]);
            }

            // ENHANCEMENT: Initialize albumArtUrl from songMetadata if available
            if (songData.albumArtUrl) {
              setAlbumArtUrl(songData.albumArtUrl);
              setOriginalAlbumArtUrl(songData.albumArtUrl);
            }

            // Initialize consolidated songMetadata state for SongsMetadataEditor
            // Include all existing fields to ensure nothing is lost during updates
            // CRITICAL FIX: Include gender fields from AI metadata if not in main songMetadata
            const aiSongMetadata = assetData.aiMetadata?.songMetadata || {};
            // LAYER-AGNOSTIC FIX: Check layerMetadata first (universal for all layers)
            const layerMetadata = assetData.aiMetadata?.layerMetadata || {};

            // CRITICAL FIX: Access raw AI response data (highest priority for gender fields)
            const rawAiResponse = layerMetadata._aiMetadata?.raw?.layerMetadata?._aiResponse || {};

            console.log('🔍 [GENDER DEBUG] Checking gender field sources:');
            console.log('  rawAiResponseGender:', {
              originalArtistGender: rawAiResponse.originalArtistGender,
              genderSuitability: rawAiResponse.genderSuitability,
              vocalType: rawAiResponse.vocalType
            });
            console.log('  songDataGender:', {
              originalArtistGender: songData.originalArtistGender,
              genderSuitability: songData.genderSuitability,
              vocalType: songData.vocalType
            });
            console.log('  layerMetadataGender:', {
              originalArtistGender: layerMetadata.originalArtistGender,
              genderSuitability: layerMetadata.genderSuitability,
              vocalType: layerMetadata.vocalType
            });
            console.log('  aiSongMetadataGender:', {
              originalArtistGender: aiSongMetadata.originalArtistGender,
              genderSuitability: aiSongMetadata.genderSuitability,
              vocalType: aiSongMetadata.vocalType
            });

            const initialSongMetadata = {
              ...songData, // Include all existing fields from main songMetadata
              // CRITICAL FIX: Ensure gender fields are preserved from AI metadata if missing
              // PRIORITY: rawAiResponse (most accurate) > layerMetadata > songMetadata > aiSongMetadata > fallback
              originalArtistGender: rawAiResponse.originalArtistGender || layerMetadata.originalArtistGender || songData.originalArtistGender || aiSongMetadata.originalArtistGender || null,
              genderSuitability: rawAiResponse.genderSuitability || layerMetadata.genderSuitability || songData.genderSuitability || aiSongMetadata.genderSuitability || '',
              vocalType: rawAiResponse.vocalType || layerMetadata.vocalType || songData.vocalType || aiSongMetadata.vocalType || '',
              // Ensure critical fields are present from various possible locations
              songName: songData.songName || (assetData as any).songName || '',
              artistName: songData.artistName || (assetData as any).artistName || '',
              albumName: songData.albumName || (assetData as any).albumName || '',
              // Preserve new API metadata fields (SongMetadataDetailsResponse)
              culturalOrigin: songData.culturalOrigin ?? '',
              variantName: songData.variantName ?? '',
              variantType: songData.variantType ?? '',
              creatorDescription: songData.creatorDescription ?? '',
              style: songData.style ?? '',
              bpm: songData.bpm ?? undefined,
              key: songData.key ?? '',
              timeSignature: songData.timeSignature ?? '',
              algorhythmMetadata: songData.algorhythmMetadata ?? undefined,
            };

            console.log('🎯 [GENDER FIX] Final songMetadata with gender fields:');
            console.log('  FINAL originalArtistGender:', initialSongMetadata.originalArtistGender);
            console.log('  FINAL genderSuitability:', initialSongMetadata.genderSuitability);
            console.log('  FINAL vocalType:', initialSongMetadata.vocalType);
            console.log('  SOURCE USED:', rawAiResponse.originalArtistGender ? 'rawAiResponse' :
                         layerMetadata.originalArtistGender ? 'layerMetadata' :
                         songData.originalArtistGender ? 'songData' : 'fallback');

            // Store in ref so it's available immediately for render
            songMetadataRef.current = initialSongMetadata;
            setSongMetadata(initialSongMetadata);
          } catch (error) {
            console.warn('Failed to parse songMetadata:', error);
          }
        } else if (assetData.layer === 'G' || assetData.layer === 'C') {
          console.log(
            '[EDIT PAGE] No songMetadata found for',
            assetData.layer,
            'layer asset, or songMetadata is falsy:',
            !!assetData.songMetadata
          );
        }

        // Initialize star metadata for S layer assets
        if (assetData.layer === 'S') {
          // CRITICAL FIX: Merge metadata sources with starMetadata taking priority (same as AssetDetailPage)
          const starData = {
            ...(assetData.aiMetadata?.layerMetadata || {}),  // AI metadata as base
            ...(assetData.aiMetadata?.starsMetadata || {}),  // Legacy AI metadata
            ...(assetData.starMetadata || {}),               // ✅ starMetadata LAST so it overrides AI nulls
          };
          setStarMetadata(starData);
          console.log('🌟 [EDIT PAGE] Star metadata initialized with merge:', starData);
          console.log('🔍 [EDIT PAGE] Star metadata sources checked:', {
            layerMetadata: assetData.aiMetadata?.layerMetadata,
            starMetadata: assetData.starMetadata,
            starsMetadata: assetData.aiMetadata?.starsMetadata,
            finalMerged: starData
          });
        }

        // Initialize moves metadata for M layer assets
        if (assetData.layer === 'M') {
          const movesData = {
            ...(assetData.aiMetadata?.layerMetadata || {}),
            ...(assetData.aiMetadata?.movesMetadata || {}),
            ...((assetData as any).movesMetadata || {}),
          };
          setMovesMetadata(applyIntelligentMovesDefaults(movesData));
          console.log('🏃 [EDIT PAGE] Moves metadata initialized:', movesData);
        }

        // Initialize worlds metadata for W layer assets
        if (assetData.layer === 'W') {
          const worldsData = {
            ...(assetData.aiMetadata?.layerMetadata || {}),
            ...(assetData.aiMetadata?.worldsMetadata || {}),
            ...((assetData as any).worldsMetadata || {}),
          };
          setWorldsMetadata(applyIntelligentWorldsDefaults(worldsData));
          console.log('🌍 [EDIT PAGE] Worlds metadata initialized:', worldsData);
        }

        // Initialize looks metadata for L layer assets
        if (assetData.layer === 'L') {
          const looksData =
            assetData.looksMetadata ||
            assetData.aiMetadata?.looksMetadata ||
            assetData.metadata?.looksMetadata;
          console.log('👗 [EDIT PAGE] Raw looksData:', looksData);

          if (looksData) {
            try {
              const parsedLooksData =
                typeof looksData === 'string'
                  ? JSON.parse(looksData)
                  : looksData;
              // CRITICAL FIX: Access nested looksMetadata from the response structure
              const actualLooksMetadata =
                parsedLooksData?.looksMetadata ||
                parsedLooksData?.data?.looksMetadata ||
                parsedLooksData;
              console.log(
                '👗 [EDIT PAGE] Looks metadata initialized:',
                actualLooksMetadata
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: actualLooksMetadata keys:',
                Object.keys(actualLooksMetadata || {})
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: styleCategory value:',
                actualLooksMetadata?.styleCategory
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: colorScheme value:',
                actualLooksMetadata?.colorScheme
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: primaryGarment value:',
                actualLooksMetadata?.primaryGarment
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: seasonality value:',
                actualLooksMetadata?.seasonality
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: occasion value:',
                actualLooksMetadata?.occasion
              );
              console.log(
                '👗 [EDIT PAGE] DEBUG: priceRange value:',
                actualLooksMetadata?.priceRange
              );

              // Helper function to normalize display values to dropdown values
              const normalizeValue = (
                backendValue: string | undefined,
                valueMap: Record<string, string>
              ): string => {
                if (!backendValue) return '';
                const normalized = Object.keys(valueMap).find(
                  key => valueMap[key] === backendValue
                );
                return (
                  normalized || backendValue.toLowerCase().replace(/\s+/g, '-')
                );
              };

              // Value mappings for backend display format to frontend dropdown values
              const seasonalityMap = {
                'all-season': 'All Season',
                spring: 'Spring',
                summer: 'Summer',
                fall: 'Fall',
                winter: 'Winter',
              };

              const priceRangeMap = {
                budget: 'Budget',
                'mid-range': 'Mid-Range',
                luxury: 'Luxury',
                designer: 'Designer',
              };

              const styleCategoryMap = {
                streetwear: 'Streetwear',
                casual: 'Casual',
                formal: 'Formal',
                athletic: 'Athletic',
                vintage: 'Vintage',
                trendy: 'Trendy',
              };

              // CRITICAL FIX: Handle both singular and plural brandName formats from backend
              const brandNameValue =
                actualLooksMetadata?.brandNames ||
                actualLooksMetadata?.brandName ||
                '';
              const brandNameExtracted = Array.isArray(brandNameValue)
                ? brandNameValue[0] || ''
                : brandNameValue || '';
              setBrandName(brandNameExtracted);

              setStyleCategory(
                normalizeValue(
                  actualLooksMetadata?.styleCategory ||
                    actualLooksMetadata?.style,
                  styleCategoryMap
                )
              );
              setPrimaryColor(
                actualLooksMetadata?.primaryColor ||
                  actualLooksMetadata?.primaryColors?.[0] ||
                  ''
              );
              setColorScheme(
                actualLooksMetadata?.colorScheme?.toLowerCase() || ''
              );
              setPrimaryGarment(
                actualLooksMetadata?.primaryGarment?.toLowerCase() || ''
              );
              setSeasonality(
                normalizeValue(actualLooksMetadata?.seasonality, seasonalityMap)
              );
              setOccasion(actualLooksMetadata?.occasion?.toLowerCase() || '');
              setTrendLevel(
                actualLooksMetadata?.trendLevel
                  ?.toLowerCase()
                  .replace(' ', '-') || ''
              );
              setFit(actualLooksMetadata?.fit || '');
              setMaterial(
                actualLooksMetadata?.material ||
                  actualLooksMetadata?.materials?.[0] ||
                  ''
              );
              setPriceRange(
                normalizeValue(actualLooksMetadata?.priceRange, priceRangeMap)
              );

              // CRITICAL FIX: Initialize accessories state from backend data
              const accessoriesValue = actualLooksMetadata?.accessories || [];
              setAccessories(
                Array.isArray(accessoriesValue)
                  ? accessoriesValue
                  : [accessoriesValue]
              );
              console.log(
                '🔧 [ACCESSORIES] Initialized accessories state:',
                accessoriesValue
              );

              console.log('👗 [EDIT PAGE] State values set:');
              console.log('  - brandNameValue (raw):', brandNameValue);
              console.log('  - brandName (extracted):', brandNameExtracted);
              console.log(
                '  - primaryColor:',
                actualLooksMetadata?.primaryColor ||
                  actualLooksMetadata?.primaryColors?.[0] ||
                  ''
              );
              console.log(
                '  - styleCategory:',
                actualLooksMetadata?.styleCategory ||
                  actualLooksMetadata?.style ||
                  ''
              );
              console.log(
                '  - colorScheme:',
                actualLooksMetadata?.colorScheme || ''
              );
              console.log(
                '  - primaryGarment:',
                actualLooksMetadata?.primaryGarment || ''
              );
              console.log(
                '  - seasonality:',
                actualLooksMetadata?.seasonality || ''
              );
              console.log('  - occasion:', actualLooksMetadata?.occasion || '');
              console.log(
                '  - priceRange:',
                actualLooksMetadata?.priceRange || ''
              );
            } catch (error) {
              console.warn('Failed to parse looksMetadata:', error);
            }
          }
        }

        // Check if this is a composite asset and analyze component metadata
        const isCompositeAssetValue =
          assetData.layer === 'C' && assetData.name.includes(':');
        setIsComposite(isCompositeAssetValue);

        if (isCompositeAssetValue) {
          console.log(
            '🔍 [COMPOSITE EDIT] Composite asset detected, analyzing component metadata...'
          );
          await analyzeComponentMetadata(assetData, isCompositeAssetValue);
        }
      } catch (error) {
        console.error('Error loading asset:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        if (errorMessage.includes('Asset not found with ID')) {
          setError(`Asset not found in development environment. This asset may exist in a different environment (staging/production). Asset ID: ${id}`);
        } else {
          setError(`Failed to load asset: ${errorMessage}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadAsset();
  }, [id]);

  // Handle star metadata changes - Enhanced for StarsMetadataEditor compatibility
  const handleStarMetadataChange = (updatedStarMetadata: any) => {
    console.log('🌟 [EDIT PAGE] Star metadata updated:', updatedStarMetadata);
    console.log(
      '🔍 [EDIT PAGE] Color Palette in update:',
      updatedStarMetadata.colorPalette
    );
    setStarMetadata(updatedStarMetadata);
  };

  const handleMovesMetadataChange = (updatedMovesMetadata: any) => {
    console.log('🏃 [EDIT PAGE] Moves metadata updated:', updatedMovesMetadata);
    setMovesMetadata(updatedMovesMetadata);
  };

  const handleWorldsMetadataChange = (updatedWorldsMetadata: any) => {
    console.log('🌍 [EDIT PAGE] Worlds metadata updated:', updatedWorldsMetadata);
    setWorldsMetadata(updatedWorldsMetadata);
  };

  // Handle looks metadata changes from unified component
  const handleLooksMetadataChange = (updatedLooksMetadata: any) => {
    console.log('👕 [EDIT PAGE] Looks metadata updated:', updatedLooksMetadata);
    console.log(
      '👕 [EDIT PAGE] Accessories in update:',
      updatedLooksMetadata.accessories
    );

    // Store the entire updated metadata for proper save operation
    setLooksMetadataState(updatedLooksMetadata);

    // Update individual state variables to maintain backward compatibility
    // CRITICAL FIX: Handle both brandNames (plural) and brandName (singular)
    const brandNameValue =
      updatedLooksMetadata.brandNames || updatedLooksMetadata.brandName || '';
    setBrandName(
      Array.isArray(brandNameValue)
        ? brandNameValue[0] || ''
        : brandNameValue || ''
    );
    setStyleCategory(updatedLooksMetadata.styleCategory || '');
    setPrimaryColor(updatedLooksMetadata.primaryColor || '');
    setColorScheme(updatedLooksMetadata.colorScheme || '');
    setPrimaryGarment(updatedLooksMetadata.primaryGarment || '');
    setSeasonality(updatedLooksMetadata.seasonality || '');
    setOccasion(updatedLooksMetadata.occasion || '');
    setTrendLevel(updatedLooksMetadata.trendLevel || '');
    setFit(updatedLooksMetadata.fit || '');
    setMaterial(updatedLooksMetadata.material || '');
    setPriceRange(updatedLooksMetadata.priceRange || '');

    // CRITICAL FIX: Store accessories in state for save operation
    if (updatedLooksMetadata.accessories) {
      setAccessories(
        Array.isArray(updatedLooksMetadata.accessories)
          ? updatedLooksMetadata.accessories
          : [updatedLooksMetadata.accessories]
      );
      console.log(
        '🔧 [ACCESSORIES] Updated accessories state:',
        updatedLooksMetadata.accessories
      );
    }
  };

  // Analyze component metadata for composite assets
  const analyzeComponentMetadata = async (
    asset: Asset,
    isCompositeAsset: boolean = false
  ) => {
    try {
      setMetadataLoading(true);
      console.log(
        '🔍 [METADATA ANALYSIS] Starting analysis for composite asset:',
        asset.name
      );

      const analysis = await componentMetadataService.analyzeCompositeMetadata(
        asset
      );
      setMetadataAnalysis(analysis);

      // Log changes status (banner will show automatically if there are changes)
      if (analysis.hasAnyChanges) {
        console.log(
          '⚠️ [METADATA ANALYSIS] Changes detected, banner will be displayed'
        );
      } else {
        console.log('✅ [METADATA ANALYSIS] No changes detected');
      }

      // Determine if Song metadata should be read-only
      // CRITICAL FIX: Always enable read-only mode for composite assets
      // Song metadata fields should never be editable in composite assets - they are inherited from components
      if (isCompositeAsset) {
        setIsReadOnlyMode(true);
        console.log(
          '🔒 [METADATA ANALYSIS] Song metadata set to read-only mode for composite asset'
        );

        const primarySongComponent =
          componentMetadataService.getPrimarySongComponent(
            analysis.componentComparisons
          );

        if (primarySongComponent) {
          console.log(
            '✅ [METADATA ANALYSIS] Components loaded successfully, sync available'
          );
        } else if (analysis.componentComparisons.length === 0) {
          // Show warning but still maintain read-only mode
          setError(
            '⚠️ Component metadata sync unavailable - some component assets may not exist or use legacy taxonomy. Song metadata is read-only to preserve data integrity.'
          );
          console.log(
            '⚠️ [METADATA ANALYSIS] Components failed to load but read-only mode enforced'
          );
        } else {
          console.log(
            'ℹ️ [METADATA ANALYSIS] Partial component loading, read-only mode enforced'
          );
        }
      }
    } catch (error) {
      console.error(
        '❌ [METADATA ANALYSIS] Error analyzing component metadata:',
        error
      );
    } finally {
      setMetadataLoading(false);
    }
  };

  // Handle metadata update from user prompt
  const handleMetadataUpdate = async () => {
    if (!metadataAnalysis || !asset) return;

    try {
      setMetadataLoading(true);
      console.log(
        '🔄 [METADATA UPDATE] Updating composite metadata with component changes'
      );

      const primarySongComponent =
        componentMetadataService.getPrimarySongComponent(
          metadataAnalysis.componentComparisons
        );

      if (primarySongComponent) {
        const updatePayload =
          componentMetadataService.createMetadataSyncPayload(
            primarySongComponent
          );

        // Update local state
        if (updatePayload.songMetadata) {
          const songData = updatePayload.songMetadata;
          setSongName(songData.songName || '');
          setArtistName(songData.artistName || '');
          setAlbumName(songData.albumName || '');
          setBpm(songData.bpm || '');
          setGenre(songData.genre || '');
          setReleaseYear(songData.releaseYear || '');
          setDuration(songData.duration || '');
          setRemixedBy(songData.remixedBy || '');
          setOriginalSongId(songData.originalSongId || '');
          setOriginalArtistGender(songData.originalArtistGender || '');
          setGenderSuitability(songData.genderSuitability || '');
          setVocalRange(songData.vocalRange || '');
          // CRITICAL FIX: Handle ageAppropriateness as string from backend (metadata sync)
          if (songData.ageAppropriateness) {
            if (typeof songData.ageAppropriateness === 'string') {
              // Backend returns string like "all-ages" - convert to frontend format
              const frontendValues = [];
              if (
                safeIncludes(songData.ageAppropriateness, 'all') ||
                safeIncludes(songData.ageAppropriateness, 'everyone')
              ) {
                frontendValues.push('all ages');
              }
              if (safeIncludes(songData.ageAppropriateness, 'teen')) {
                frontendValues.push('teen+');
              }
              if (
                safeIncludes(songData.ageAppropriateness, 'adult') &&
                !safeIncludes(songData.ageAppropriateness, 'young')
              ) {
                frontendValues.push('adult');
              }
              if (safeIncludes(songData.ageAppropriateness, 'young adult')) {
                frontendValues.push('young adult');
              }
              setAgeAppropriateness(
                frontendValues.length > 0 ? frontendValues : ['all ages']
              );
            } else if (Array.isArray(songData.ageAppropriateness)) {
              setAgeAppropriateness(songData.ageAppropriateness);
            } else {
              setAgeAppropriateness([]);
            }
          } else {
            setAgeAppropriateness([]);
          }

          if (songData.albumArtUrl) {
            setAlbumArtUrl(songData.albumArtUrl);
            setOriginalAlbumArtUrl(songData.albumArtUrl);
          }
        }

        console.log(
          '✅ [METADATA UPDATE] Local state updated with component metadata'
        );
      }

      // Clear analysis after successful update
      setMetadataAnalysis(null);
    } catch (error) {
      console.error('❌ [METADATA UPDATE] Error updating metadata:', error);
      setError('Failed to update component metadata');
    } finally {
      setMetadataLoading(false);
    }
  };

  // Handle keeping current metadata
  const handleKeepCurrentMetadata = () => {
    console.log('📌 [METADATA UPDATE] User chose to keep current metadata');
    setMetadataAnalysis(null);
  };

  const handleSave = async () => {
    if (!asset || !id) return;

    try {
      setSaving(true);
      setError(null);
      // Backend now supports both MongoDB ID and HFN for the :id parameter
      // Use the ID from URL which is consistent with how the asset was loaded
      const assetIdentifier = id;
      console.log(
        '🔍 Update attempt - using ID from URL:',
        assetIdentifier,
        '(asset.name:',
        asset.name,
        ')'
      );
      // Phase 2B: Send correct field mapping to backend
      const updateData: any = {
        description: description, // Store AI-generated description in description field
        // NOTE: creatorDescription for G layer goes in songMetadata, not root level
        // For non-G layers, it goes at root level (see below)
        tags,
      };

      // Add creatorDescription at root level for non-G layers
      if (asset.layer !== 'G') {
        updateData.creatorDescription = creatorDescription;
      }

      // REMOVED: Duplicate album art handling - now handled via songMetadata object
      // Album art updates are managed by SongsMetadataEditor and included in songMetadata

      // Phase 2C: Include songMetadata for Songs and Composite layer assets
      if (asset.layer === 'G') {
        // For G layer assets, use the modern SongsMetadataEditor state
        if (songMetadata) {
          // Ensure we have all required fields
          const finalSongMetadata = {
            ...(asset.songMetadata || {}), // Original data
            ...songMetadata, // Updated data from editor
          };

          // CRITICAL FIX: Only include creatorDescription if it actually changed
          // This prevents false positives in version notes
          const originalCreatorDesc = (asset.songMetadata as any)?.creatorDescription || '';
          const currentCreatorDesc = creatorDescription || '';
          if (currentCreatorDesc !== originalCreatorDesc) {
            finalSongMetadata.creatorDescription = currentCreatorDesc;
          }

          // CRITICAL WORKAROUND: Remove language field to avoid backend validation error
          // Backend error: "found language override field in document with non-string type"
          if ('language' in finalSongMetadata) {
            delete finalSongMetadata.language;
            console.log(
              '🚨 [LANGUAGE WORKAROUND] Removed language field from update payload to avoid backend error'
            );
          }

          // CRITICAL FIX: Extract AlgoRhythm fields from songMetadata and send at ROOT level
          // SongsMetadataEditor returns AlgoRhythm fields in songMetadata, but backend expects them at root.
          // API AlgorhythmMetadataRequest has 7 fields: genre, mood, targetAudience, ageAppropriateness, culturalContext, musicalStyle, performanceContext (no energyLevel).
          const algorhythmFields = [
            'performanceContext',
            'targetAudience',
            'culturalContext',
            'musicalStyle',
            'ageAppropriateness'
          ];

          const toArr = (v: unknown): string[] => {
            if (Array.isArray(v)) return v.length ? v.map(String) : [];
            if (typeof v === 'string' && v.trim() !== '') return [v.trim()];
            return [];
          };

          const algorhythmMetadata: any = {};
          let hasAlgorhythmData = false;

          algorhythmFields.forEach(field => {
            if (finalSongMetadata[field] !== undefined) {
              algorhythmMetadata[field] = finalSongMetadata[field];
              hasAlgorhythmData = true;
              delete finalSongMetadata[field];
            }
          });
          // API requires genre and mood in algorhythmMetadata; include from songMetadata (normalized to arrays)
          const algoGenre = toArr(finalSongMetadata.genre);
          const algoMood = toArr(finalSongMetadata.mood);
          if (algoGenre.length > 0 || algoMood.length > 0) {
            if (algoGenre.length > 0) algorhythmMetadata.genre = algoGenre;
            if (algoMood.length > 0) algorhythmMetadata.mood = algoMood;
            hasAlgorhythmData = true;
          }

          // Include algorhythmMetadata at root level if any fields were found
          if (hasAlgorhythmData) {
            updateData.algorhythmMetadata = algorhythmMetadata;
            console.log('🎯 [ALGORHYTHM METADATA] Extracted from songMetadata and sending at root level:', algorhythmMetadata);
          }

          updateData.songMetadata = finalSongMetadata;
          console.log('🎵 [SONG METADATA - G LAYER] Final update payload:', {
            original: asset.songMetadata,
            updates: songMetadata,
            final: finalSongMetadata,
            algorhythmMetadata: updateData.algorhythmMetadata
          });
        } else {
          console.warn(
            '🚨 [SONG METADATA - G LAYER] No songMetadata state found'
          );
        }
      } else if (asset.layer === 'C') {
        // For C layer assets, use the legacy individual fields
        const legacySongMetadata = {
          songName: songName || null,
          artistName: artistName || null,
          albumName: albumName || null,
          albumArtUrl: albumArtUrl || null,
          bpm: bpm ? Number(bpm) : null,
          genre: genre || null,
          releaseYear: releaseYear ? Number(releaseYear) : null,
          duration: duration ? Number(duration) : null,
          // Composite-specific fields
          remixedBy: remixedBy || null,
          originalSongId: originalSongId || null,
          // NEW: Gender information fields
          originalArtistGender: originalArtistGender || null,
          genderSuitability: genderSuitability || null,
          vocalRange: vocalRange || null,
          ageAppropriateness:
            ageAppropriateness.length > 0 ? ageAppropriateness : null,
        };
        updateData.songMetadata = legacySongMetadata;
        console.log(
          '🎵 [SONG METADATA - C LAYER] Using legacy fields:',
          legacySongMetadata
        );
      }

      // Include star metadata for Stars layer assets - Enhanced for StarsMetadataEditor
      if (asset.layer === 'S' && starMetadata) {
        // CRITICAL FIX: Flatten physicalTraits if they're nested (StarsMetadataEditor format)
        let flattenedStarMetadata = { ...starMetadata };

        if (starMetadata.physicalTraits) {
          console.log(
            '🔧 [STAR METADATA] Flattening physicalTraits for backend:',
            starMetadata.physicalTraits
          );
          // Flatten physicalTraits to root level for backend compatibility
          Object.assign(flattenedStarMetadata, starMetadata.physicalTraits);
          delete flattenedStarMetadata.physicalTraits;
        }

        // CRITICAL FIX: Remove versioning fields that should not be in update payload
        // Backend expects these to be managed server-side only
        delete flattenedStarMetadata.versionCreatedAt;
        delete flattenedStarMetadata.versionCreatedBy;
        delete flattenedStarMetadata.versionNotes;

        updateData.starMetadata = flattenedStarMetadata;
        console.log(
          '🌟 [STAR METADATA] Updating star metadata (flattened):',
          flattenedStarMetadata
        );
        console.log(
          '🔍 [STAR METADATA] Hair Color value being sent:',
          flattenedStarMetadata.hairColor
        );
        console.log(
          '🔍 [STAR METADATA] Hair Style value being sent:',
          flattenedStarMetadata.hairStyle
        );
        console.log(
          '🔍 [STAR METADATA] Makeup Type value being sent:',
          flattenedStarMetadata.makeupType
        );
        console.log(
          '🔍 [STAR METADATA] Color Palette value being sent:',
          flattenedStarMetadata.colorPalette
        );
        console.log(
          '🔍 [STAR METADATA] Vibe value being sent:',
          flattenedStarMetadata.vibe
        );
        console.log(
          '🔍 [STAR METADATA] Energy Level value being sent:',
          flattenedStarMetadata.energy
        );
        console.log(
          '🔍 [STAR METADATA] Full flattened keys:',
          Object.keys(flattenedStarMetadata)
        );
      }

      // Include looks metadata for Looks layer assets
      if (asset.layer === 'L') {
        const looksMetadata = {
          // CRITICAL FIX: Send brandNames as array (plural) to match backend schema
          brandNames: brandName ? [brandName] : null,
          styleCategory: styleCategory || null,
          primaryColor: primaryColor || null,
          colorScheme: colorScheme || null,
          primaryGarment: primaryGarment || null,
          seasonality: seasonality || null,
          occasion: occasion || null,
          trendLevel: trendLevel || null,
          fit: fit || null,
          material: material || null,
          priceRange: priceRange || null,
          accessories:
            accessories && accessories.length > 0 ? accessories : null,
        };
        updateData.looksMetadata = looksMetadata;
        console.log(
          '👗 [LOOKS METADATA] Updating looks metadata:',
          looksMetadata
        );
        console.log(
          '👗 [CRITICAL DEBUG] Individual state values at save time:'
        );
        console.log('  - brandName state:', brandName);
        console.log('  - primaryColor state:', primaryColor);
        console.log('  - styleCategory state:', styleCategory);
        console.log('  - colorScheme state:', colorScheme);
        console.log('  - accessories state:', accessories);
        console.log(
          '  - accessories in looksMetadata:',
          looksMetadata.accessories
        );
      }

      // Layer-specific PUT (AssetUpdateRequest: description, tags, metadata only) for G,S,L,M,W
      const LAYERS_NEW_UPDATE = ['G', 'S', 'L', 'M', 'W'];
      if (LAYERS_NEW_UPDATE.includes(asset.layer)) {
        const descTrimmed = (description ?? '').trim();
        const tagsValid = Array.isArray(tags) && tags.length > 0 && tags.some(t => (t ?? '').trim() !== '');
        if (!descTrimmed || !tagsValid) {
          setError('Description and at least one tag are required.');
          setSaving(false);
          return;
        }
        const updateBody = buildLayerAssetUpdateRequest({
          layer: asset.layer as 'G' | 'S' | 'L' | 'M' | 'W',
          description: descTrimmed,
          tags,
          songMetadata,
          starMetadata,
          looksMetadata: looksMetadataState,
          movesMetadata,
          worldsMetadata,
        });
        try {
          await assetService.updateAssetMetadata(asset.layer, assetIdentifier, updateBody);
          const updatedAsset = await assetService.getAssetById(assetIdentifier);
          setAsset(updatedAsset);
          window.dispatchEvent(new CustomEvent('assetUpdated', {
            detail: { assetName: updatedAsset.name, assetId: updatedAsset.id, version: (updatedAsset as any).version, action: 'edit' },
          }));
          navigate(`/assets/${id}`);
        } catch (updateErr: any) {
          console.error('Error updating asset metadata:', updateErr);
          setError(updateErr?.message ?? 'Failed to update asset');
        } finally {
          setSaving(false);
        }
        return;
      }

      // CRITICAL FIX (Phase 0): Remove ALL versioning fields from update payload
      // Backend manages these fields automatically - frontend should never send them
      delete updateData.versionCreatedAt;
      delete updateData.versionNumber;
      delete updateData.versionCreatedBy;
      delete updateData.previousVersionId;
      delete updateData.isLatestVersion;

      // Generate meaningful version notes based on what changed
      const versionNotes = generateVersionNotes(asset, updateData);
      updateData.versionNotes = versionNotes;

      console.log('🔒 [VERSION FIELDS REMOVED] Update payload after version field cleanup:', Object.keys(updateData));
      console.log('📝 [VERSION NOTES] Generated version notes:', versionNotes);

      await assetService.updateAsset(assetIdentifier, updateData);

      // CRITICAL FIX: Reload asset data after successful update to prevent two-save-cycle issue
      // This ensures the component has the latest data and doesn't require a second save
      console.log(
        '🔄 [EDIT RELOAD] Reloading asset data after successful update'
      );
      try {
        const updatedAsset = await assetService.getAssetById(assetIdentifier);
        setAsset(updatedAsset);
        console.log('✅ [EDIT RELOAD] Asset data reloaded successfully');
      } catch (reloadError) {
        console.warn(
          '⚠️ [EDIT RELOAD] Failed to reload asset data:',
          reloadError
        );
        // Continue with navigation even if reload fails
      }

      navigate(`/assets/${id}`);
    } catch (error) {
      console.error('Error updating asset:', error);
      setError('Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/assets/${id}`);
  };

  const handleRegenerateData = async () => {
    if (!asset || !id) return;
    const layer = asset.layer;
    if (!['G', 'S', 'L', 'M', 'W'].includes(layer)) return;
    const fileUrl = asset.imageUrl || (asset.metadata as { fileUrl?: string })?.fileUrl;
    if (!fileUrl) {
      setRegenerateError('No file URL available for this asset.');
      return;
    }
    setRegenerateError(null);
    setRegenerating(true);
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);
      const blob = await response.blob();
      const ext = layer === 'G' ? 'mp3' : layer === 'L' || layer === 'S' ? 'jpg' : 'mp4';
      const file = new File([blob], `asset.${ext}`, { type: blob.type || undefined });
      const result = await aiContractService.generateMetadata({
        layer,
        creatorDescription,
        file,
      });
      if (result.data.description !== undefined) setDescription(result.data.description);
      if (result.data.tags !== undefined) setTags(result.data.tags);
      const layerMeta = result.data.layerMetadata;
      if (layerMeta) {
        if (asset.layer === 'G') {
          setSongMetadata((prev: any) => ({ ...(prev || {}), ...layerMeta }));
        } else if (asset.layer === 'S') {
          setStarMetadata((prev: any) => ({ ...(prev || {}), ...layerMeta }));
        } else if (asset.layer === 'L') {
          handleLooksMetadataChange(layerMeta);
        }
      }
      setAsset(prev => {
        if (!prev) return prev;
        const next = { ...prev } as any;
        if (layerMeta && prev.layer === 'G') {
          next.songMetadata = { ...(prev.songMetadata || {}), ...layerMeta };
        }
        if (layerMeta && prev.layer === 'S') {
          next.starMetadata = { ...(prev.starMetadata || {}), ...layerMeta };
        }
        if (layerMeta && prev.layer === 'L') {
          const merged = { ...(prev.looksMetadata || {}), ...result.data.layerMetadata };
          next.looksMetadata = {
            ...merged,
            assetType: (merged.assetType ?? 'base') as 'base' | 'variant',
            styleCategory: merged.styleCategory ?? '',
            colorScheme: merged.colorScheme ?? '',
            primaryGarment: merged.primaryGarment ?? '',
            seasonality: merged.seasonality ?? '',
            occasion: merged.occasion ?? '',
            accessories: Array.isArray(merged.accessories) ? merged.accessories : [],
          };
        }
        if (layerMeta && prev.layer === 'M') {
          next.movesMetadata = { ...(prev.movesMetadata || {}), ...layerMeta };
        }
        if (layerMeta && prev.layer === 'W') {
          next.worldsMetadata = { ...(prev.worldsMetadata || {}), ...layerMeta };
        }
        if (result.data.description !== undefined) next.description = result.data.description;
        if (result.data.tags !== undefined) next.tags = result.data.tags;
        return next;
      });
    } catch (e) {
      setRegenerateError(e instanceof Error ? e.message : 'Regenerate data failed');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        {error.includes('Asset not found in development environment') && (
          <Alert severity="info" sx={{ mb: 2 }}>
            💡 <strong>Tips:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Try searching for similar assets in this environment</li>
              <li>Check if the asset exists in staging/production environments</li>
              <li>Create a new asset if needed using the registration wizard</li>
            </ul>
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={() => navigate('/search-assets')} variant="outlined">
            Search Assets
          </Button>
          <Button onClick={() => navigate('/register-asset')} variant="contained">
            Register New Asset
          </Button>
        </Box>
      </Container>
    );
  }

  if (!asset) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Asset not found
        </Alert>
        <Button onClick={() => navigate('/search-assets')} variant="outlined">
          Back to Search
        </Button>
      </Container>
    );
  }

  if (asset.layer === 'C') {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Composite assets are no longer editable in backoffice.
        </Alert>
        <Button onClick={() => navigate('/search-assets')} variant="outlined">
          Back to Browse Assets
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header row: back, title, layer chip, taxonomy chip */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 3, pb: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate(`/assets/${id}`)} aria-label="Back to asset" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>
            Edit: {formatCompositeHfn(asset.name)}
          </Typography>
          <Chip label={LAYER_LABELS[asset.layer] ?? asset.layer} color="primary" variant="outlined" size="medium" />
          {taxonomyPath && <Chip label={taxonomyPath} variant="outlined" size="small" />}
          <Box sx={{ flexGrow: 1 }} />
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 4 }}>
      {/* Basic information - read-only, same style as AssetDetailPage */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Basic information
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Human-Friendly Name (HFN)
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
            {formattedHFN || formatCompositeHfn(asset.name) || asset.name || '—'}
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Machine-Friendly Address (MFA)
          </Typography>
          <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
            {formatCompositeMfa(getMFA()) || getMFA() || '—'}
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Category / Subcategory
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(asset.category || asset.categoryCode) && (
              <Chip label={asset.category || asset.categoryCode} size="small" variant="outlined" />
            )}
            {(asset.subcategory || asset.subcategoryCode) && (
              <Chip label={asset.subcategory || asset.subcategoryCode} size="small" variant="outlined" />
            )}
            {!(asset.category || asset.categoryCode) && !(asset.subcategory || asset.subcategoryCode) && (
              <Typography variant="body2" color="text.secondary">—</Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Replace File Section - only for layers that still use legacy replace (e.g. composite); G,S,L,M,W use /assets/:id/edit-file */}
      {!isReadOnlyMode && !['G', 'S', 'L', 'M', 'W'].includes(asset.layer) && (
        <ReplaceFileSection
          asset={asset}
          onFileReplaced={async () => {
            console.log('🔄 [EDIT PAGE] File replaced, reloading asset');
            try {
              const assetData = await assetService.getAssetById(id!);
              setAsset(assetData);
              console.log('✅ [EDIT PAGE] Asset reloaded successfully');
            } catch (error) {
              console.error('❌ [EDIT PAGE] Failed to reload asset:', error);
            }
          }}
        />
      )}

      {/* Component Changes Banner for Composite Assets */}
      {metadataAnalysis && metadataAnalysis.hasAnyChanges && (
        <ComponentChangesBanner
          analysis={metadataAnalysis}
          context="edit"
          onUpdate={handleMetadataUpdate}
          onDismiss={handleKeepCurrentMetadata}
          loading={metadataLoading}
        />
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {/* Creator's Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            Creator's Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is the original description provided by the creator when the
            asset was registered. It is sent to the AI when you click Regenerate data.
          </Typography>
          <TextField
            value={creatorDescription}
            onChange={e => setCreatorDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            placeholder="Enter creator's description..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.06)',
                },
                '&.Mui-focused': {
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                },
              },
            }}
          />
          {!isReadOnlyMode && ['G', 'S', 'L', 'M', 'W'].includes(asset.layer) && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRegenerateData}
                disabled={regenerating}
              >
                {regenerating ? <CircularProgress size={20} /> : 'Regenerate data'}
              </Button>
              {regenerateError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setRegenerateError(null)}>
                  {regenerateError}
                </Alert>
              )}
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* AI-Generated Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, color: 'success.main' }}
          >
            AI-Generated Description
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            AI-generated description that enhances searchability and provides
            additional context. This description is optimized for AlgoRhythm
            recommendations.
          </Typography>
          <TextField
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={4}
            placeholder="Enter description..."
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(46, 125, 50, 0.04)',
                '&:hover': {
                  bgcolor: 'rgba(46, 125, 50, 0.06)',
                },
                '&.Mui-focused': {
                  bgcolor: 'rgba(46, 125, 50, 0.08)',
                },
              },
            }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Tags Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Tags
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => setTags(tags.filter((_, i) => i !== index))}
                variant="outlined"
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Add tag"
              value={newTag}
              onChange={e => setNewTag(e.target.value)}
              size="small"
              onKeyPress={e => {
                if (e.key === 'Enter' && newTag.trim()) {
                  setTags([...tags, newTag.trim()]);
                  setNewTag('');
                }
              }}
            />
            <Button
              onClick={() => {
                if (newTag.trim()) {
                  setTags([...tags, newTag.trim()]);
                  setNewTag('');
                }
              }}
              variant="outlined"
              size="small"
            >
              Add
            </Button>
          </Box>
        </Box>

        {/*
          REFACTORED: Album Art is now handled by layer-specific metadata components
          - Songs (G): SongsMetadataEditor handles album art
          - Stars (S): StarsMetadataEditor handles profile images
          - Looks (L): LooksInformation handles look-specific imagery
          - etc.

          This standalone Album Art section was removed to maintain layer independence
        */}

        {/* Looks Information for L layer assets - UNIFIED COMPONENT */}
        {asset?.layer === 'L' && (
          <LooksInformation
            asset={{
              ...asset,
              // Merge current state back into asset for proper data access
              looksMetadata: {
                // Start with AI metadata as base
                ...(asset.looksMetadata || {}),
                // Override with current state (user's edits take priority)
                brandNames: brandName,
                styleCategory: styleCategory,
                primaryColor: primaryColor,
                colorScheme: colorScheme,
                primaryGarment: primaryGarment,
                seasonality: seasonality,
                occasion: occasion,
                accessories: accessories,
              },
            }}
            isEditable={!isReadOnlyMode}
            onMetadataChange={handleLooksMetadataChange}
          />
        )}

        {/* Song Information for G layer assets - Use SongsMetadataEditor (editable) */}
        {(asset?.layer as string) === 'G' && songMetadata && (
          <>
            <Divider sx={{ mt: 3, mb: 3 }} />

            <SongsMetadataEditor
              layerMetadata={(() => {
                // CRITICAL FIX: Use ref for immediate access to corrected metadata
                // songMetadata state may not be updated yet due to React async setState
                const correctedMetadata = songMetadataRef.current || songMetadata || {};
                const layerData = {
                  ...DEFAULT_SONGS_METADATA,
                  ...(asset.songMetadata || {}),
                  ...correctedMetadata,  // Use ref data which has corrected gender
                };
                console.log('🔍 [EDIT PAGE] Data being passed to SongsMetadataEditor:', {
                  assetSongMetadata: asset.songMetadata,
                  correctedSongMetadata: correctedMetadata,
                  finalLayerData: layerData,
                  finalGender: layerData.originalArtistGender,
                  usingRef: !!songMetadataRef.current
                });
                return layerData;
              })()}
              asset={asset}
              isEditable={true}
              aiGeneratedDescription={description}
              onMetadataChange={updatedMetadata => {
                console.log(
                  '🎵 [EDIT PAGE] Song metadata updated:',
                  updatedMetadata
                );
                // CRITICAL FIX: Merge with corrected songMetadata state, NOT corrupted asset.songMetadata
                // asset.songMetadata may have backend-corrupted values (e.g., "Male" instead of "Female")
                // songMetadata state has the corrected values from rawAiResponse
                const mergedMetadata = {
                  ...(songMetadata || {}), // ✅ Use corrected state, not corrupted asset data
                  ...updatedMetadata,      // Apply updates from editor
                };
                console.log('🔧 [GENDER PRESERVATION] Merge sources:', {
                  corruptedAssetGender: asset.songMetadata?.originalArtistGender,
                  correctedStateGender: songMetadata?.originalArtistGender,
                  updateGender: updatedMetadata.originalArtistGender,
                  finalGender: mergedMetadata.originalArtistGender
                });
                setSongMetadata(mergedMetadata);
              }}
            />
          </>
        )}

        {/* Star Information for S layer assets - Use comprehensive StarsMetadataEditor (editable) */}
        {(asset?.layer as string) === 'S' && (
          <>
            <Divider sx={{ mt: 3, mb: 3 }} />

            <StarsMetadataEditor
              layerMetadata={{
                ...DEFAULT_STARS_METADATA,
                ...(asset.starMetadata || {}),
                ...(starMetadata || {}),
                // CRITICAL FIX: Include root-level musical style
                ...((asset as any).musicalStyle && {
                  musicalStyle: (asset as any).musicalStyle,
                }),
              }}
              asset={asset}
              isEditable={true}
              onMetadataChange={handleStarMetadataChange}
            />
          </>
        )}

        {/* Moves Information for M layer assets */}
        {(asset?.layer as string) === 'M' && (
          <>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <MovesMetadataEditor
              layerMetadata={movesMetadata || (asset as any).movesMetadata || {}}
              asset={asset}
              isEditable={true}
              onMetadataChange={handleMovesMetadataChange}
            />
          </>
        )}

        {/* Worlds Information for W layer assets */}
        {(asset?.layer as string) === 'W' && (
          <>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <WorldsMetadataEditor
              layerMetadata={worldsMetadata || (asset as any).worldsMetadata || {}}
              asset={asset}
              isEditable={true}
              onMetadataChange={handleWorldsMetadataChange}
            />
          </>
        )}

        {/* Algorhythm Metadata Display for Individual Assets (S, G, L, M, W layers) */}
        {['S', 'G', 'L', 'M', 'W'].includes(asset.layer) && asset.algorhythmMetadata && (() => {
          // CRITICAL FIX (Bug #27): Use helper to merge complete AlgoRhythm metadata from all sources
          // This works around backend bug where root-level algorhythmMetadata is incomplete
          const completeMetadata = getCompleteAlgorhythmMetadata(asset);
          return completeMetadata ? (
            <>
              <Divider sx={{ mt: 3, mb: 3 }} />
              <AlgorhythmMetadataDisplay
                metadata={completeMetadata}
                title="🎯 Algorhythm Metadata"
                subtitle="Enhanced metadata for this asset (read-only)"
              />
            </>
          ) : null;
        })()}

        {/* Album Art Section continues below for C layers only (G layer uses SongsMetadataEditor) */}
        {asset.layer === 'C' && (
          <>
            {/* Algorhythm Metadata Display for Composite Assets */}
            {asset.algorhythmMetadata && (() => {
              // CRITICAL FIX (Bug #27): Use helper to merge complete AlgoRhythm metadata from all sources
              // This works around backend bug where root-level algorhythmMetadata is incomplete
              const completeMetadata = getCompleteAlgorhythmMetadata(asset);
              return completeMetadata ? (
                <>
                  <Divider sx={{ mt: 3, mb: 3 }} />
                  <AlgorhythmMetadataDisplay
                    metadata={completeMetadata}
                    title="🎯 Algorhythm Metadata"
                    subtitle="Aggregated from all 5 component assets (read-only)"
                  />
                </>
              ) : null;
            })()}

            {/* Aggregated Metadata Display for Composite Assets */}
            {asset.aggregatedMetadata && (
              <AggregatedMetadataDisplay
                metadata={asset.aggregatedMetadata as AggregatedMetadata}
                title="✨ Component Synergy Analysis"
                subtitle="Compatibility scores showing how well the 5 components work together"
              />
            )}

            {/* Component Metadata Sections - Display all 5 component metadata (read-only) */}
            {(() => {
              // Extract component IDs from asset.components array or parse from composite name
              let componentIds: string[] = [];

              if (asset.components && asset.components.length > 0) {
                // Use existing components array
                componentIds = asset.components.map((c: any) => c.id || c._id || c);
              } else if (asset.name && asset.name.includes(':')) {
                // Fallback: Parse component IDs from composite name
                // Format: C.CAT.SUB.001:1.001.001.001+2.002.002.002+3.003.003.003+4.004.004.004+5.005.005.005
                const parts = asset.name.split(':');
                if (parts.length === 2) {
                  componentIds = parts[1].split('+').map(id => id.trim());
                  console.log('🔧 [COMPOSITE FIX] Parsed component IDs from asset name:', componentIds);
                }
              }

              return componentIds.length >= 5 ? (
                <>
                  <Divider sx={{ mt: 3, mb: 3 }} />
                  <ComponentMetadataSections
                    componentIds={componentIds}
                    compositeAsset={asset}
                  />
                </>
              ) : null;
            })()}

            {/* Phase 2C: Song Metadata Fields for C layer (Composite) assets */}
            {/* HIDDEN FOR COMPOSITES: Component metadata is now displayed via ComponentMetadataSections */}
            {!isComposite && (
              <>
            <Divider sx={{ mt: 3, mb: 3 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: 'primary.main' }}>
                🎵{' '}
                {asset?.layer === 'C' ? 'Song/Remix Metadata' : 'Song Metadata'}
              </Typography>
              {isComposite && isReadOnlyMode && (
                <Chip
                  label="Inherited from Components"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>

            {/* Base Asset Type Display for Songs C layer (similar to Stars layer) */}
            {(() => {
              const songData = asset.songMetadata;
              const isVariant = songData?.assetType === 'variant';
              const baseAssetId = songData?.baseSongId;

              return (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Chip
                      label={isVariant ? 'Variant Asset' : 'Base Asset'}
                      color={isVariant ? 'secondary' : 'primary'}
                      size="small"
                      icon={
                        isVariant ? (
                          <Box component="span">🔗</Box>
                        ) : (
                          <Box component="span">🎵</Box>
                        )
                      }
                    />
                  </Box>

                  {/* Base Asset ID display (for variants only) */}
                  {baseAssetId && isVariant && (
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'info.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'info.200',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Base Asset:
                      </Typography>
                      <BaseSongIdDisplay baseSongId={baseAssetId} />
                    </Box>
                  )}

                  {/* Variant Name (for variants only) */}
                  {songData?.variantName && isVariant && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: 'warning.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.200',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Variant Name:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {songData.variantName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {asset?.layer === 'C'
                ? isReadOnlyMode
                  ? 'Song metadata inherited from component assets. This information is read-only to maintain data integrity with source components.'
                  : 'Song metadata and remix information for this composite asset.'
                : 'Structured song data for enhanced searchability and AlgoRhythm compatibility.'}
            </Typography>

            {/* Read-only alert for composite assets */}
            {isComposite && isReadOnlyMode && (
              <Alert
                severity={
                  error && error.includes('legacy taxonomy')
                    ? 'warning'
                    : 'info'
                }
                sx={{ mb: 3 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {error && error.includes('legacy taxonomy')
                    ? 'Legacy Composite Asset'
                    : 'Component Metadata Sync Active'}
                </Typography>
                <Typography variant="body2">
                  {error && error.includes('legacy taxonomy')
                    ? 'This composite asset uses legacy component references. Song metadata is read-only to preserve data integrity. Consider re-creating this composite with current assets.'
                    : 'Song metadata fields are read-only because they are inherited from component assets. To change this data, edit the original Song component asset.'}
                </Typography>
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Song Name"
                  value={songName}
                  onChange={e => setSongName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g., Party in the USA"
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Artist Name"
                  value={artistName}
                  onChange={e => setArtistName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g., Miley Cyrus"
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Album Name"
                  value={albumName}
                  onChange={e => setAlbumName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g., The Time of Our Lives"
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="BPM"
                  type="number"
                  value={bpm}
                  onChange={e =>
                    setBpm(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  fullWidth
                  variant="outlined"
                  placeholder="120"
                  inputProps={{ min: 60, max: 200 }}
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Genre"
                  value={genre}
                  onChange={e => setGenre(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="e.g., pop"
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Release Year"
                  type="number"
                  value={releaseYear}
                  onChange={e =>
                    setReleaseYear(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  fullWidth
                  variant="outlined"
                  placeholder="2009"
                  inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label="Duration (seconds)"
                  type="number"
                  value={duration}
                  onChange={e =>
                    setDuration(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  fullWidth
                  variant="outlined"
                  placeholder="210"
                  inputProps={{ min: 1, max: 600 }}
                  disabled={isReadOnlyMode}
                  InputProps={{
                    sx: isReadOnlyMode
                      ? {
                          bgcolor: 'action.disabledBackground',
                          '& .MuiInputBase-input.Mui-disabled': {
                            color: 'text.primary',
                            WebkitTextFillColor: 'unset',
                          },
                        }
                      : {},
                  }}
                  helperText={
                    isReadOnlyMode ? 'Inherited from Song component' : undefined
                  }
                />
              </Grid>

              {/* Remix-specific fields - for songs that are remixes */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Remixed By"
                  value={remixedBy}
                  onChange={e => setRemixedBy(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="Username of the remixer (if this is a remix)"
                  helperText="Optional - only fill if this song is a remix of another song"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Original Song ID"
                  value={originalSongId}
                  onChange={e => setOriginalSongId(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="G.POP.TEN.001 (if this is a remix)"
                  helperText="Optional - reference to the original song asset if this is a remix"
                />
              </Grid>

              {/* NEW: Gender Information Fields for Songs Layer - DISABLED (now handled by SongsMetadataEditor) */}
              {false && asset?.layer === 'G' && (
                <>
                  {isReadOnlyMode && (
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}
                      >
                        Gender information inherited from Song component - edit
                        the source component to make changes
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
              {false && asset?.layer === 'G' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{ color: 'secondary.main', fontWeight: 600 }}
                      >
                        🎭 Gender Information
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Enhanced metadata for AlgoRhythm compatibility and
                        improved searchability.
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Original Artist Gender */}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Original Artist Gender
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[
                          { value: 'male', label: 'Male' },
                          { value: 'female', label: 'Female' },
                          { value: 'group', label: 'Group' },
                          { value: 'Non-binary', label: 'Non-binary' },
                          { value: 'unknown', label: 'Unknown' },
                        ].map(option => (
                          <Chip
                            key={option.value}
                            label={option.label}
                            variant={
                              originalArtistGender === option.value
                                ? 'filled'
                                : 'outlined'
                            }
                            color={
                              originalArtistGender === option.value
                                ? 'primary'
                                : 'default'
                            }
                            onClick={
                              isReadOnlyMode
                                ? undefined
                                : () =>
                                    setOriginalArtistGender(option.value as any)
                            }
                            sx={{
                              cursor: isReadOnlyMode ? 'default' : 'pointer',
                              opacity: isReadOnlyMode ? 0.7 : 1,
                            }}
                            disabled={isReadOnlyMode}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Gender Suitability */}
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Gender Suitability (Who can perform this song?)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[
                          { value: 'male', label: 'Male Only' },
                          { value: 'female', label: 'Female Only' },
                          { value: 'unisex', label: 'Unisex (Anyone)' },
                        ].map(option => (
                          <Chip
                            key={option.value}
                            label={option.label}
                            variant={
                              genderSuitability === option.value
                                ? 'filled'
                                : 'outlined'
                            }
                            color={
                              genderSuitability === option.value
                                ? 'secondary'
                                : 'default'
                            }
                            onClick={
                              isReadOnlyMode
                                ? undefined
                                : () =>
                                    setGenderSuitability(option.value as any)
                            }
                            sx={{
                              cursor: isReadOnlyMode ? 'default' : 'pointer',
                              opacity: isReadOnlyMode ? 0.7 : 1,
                            }}
                            disabled={isReadOnlyMode}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Vocal Range */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Vocal Range
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {[
                          { value: 'low', label: 'Low' },
                          { value: 'medium', label: 'Medium' },
                          { value: 'high', label: 'High' },
                        ].map(option => (
                          <Chip
                            key={option.value}
                            label={option.label}
                            variant={
                              vocalRange === option.value
                                ? 'filled'
                                : 'outlined'
                            }
                            color={
                              vocalRange === option.value ? 'info' : 'default'
                            }
                            onClick={
                              isReadOnlyMode
                                ? undefined
                                : () => setVocalRange(option.value as any)
                            }
                            sx={{
                              cursor: isReadOnlyMode ? 'default' : 'pointer',
                              opacity: isReadOnlyMode ? 0.7 : 1,
                            }}
                            disabled={isReadOnlyMode}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Age Appropriateness */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Age Appropriateness
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        {['all ages', 'teen+', 'young adult', 'adult'].map(
                          age => (
                            <Chip
                              key={age}
                              label={age.charAt(0).toUpperCase() + age.slice(1)}
                              variant={
                                ageAppropriateness.includes(age)
                                  ? 'filled'
                                  : 'outlined'
                              }
                              color={
                                ageAppropriateness.includes(age)
                                  ? 'warning'
                                  : 'default'
                              }
                              onClick={
                                isReadOnlyMode
                                  ? undefined
                                  : () => {
                                      if (ageAppropriateness.includes(age)) {
                                        setAgeAppropriateness(
                                          ageAppropriateness.filter(
                                            a => a !== age
                                          )
                                        );
                                      } else {
                                        setAgeAppropriateness([
                                          ...ageAppropriateness,
                                          age,
                                        ]);
                                      }
                                    }
                              }
                              sx={{
                                cursor: isReadOnlyMode ? 'default' : 'pointer',
                                opacity: isReadOnlyMode ? 0.7 : 1,
                              }}
                              disabled={isReadOnlyMode}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </>
        )}
              </>
            )}

      </Paper>

        </Box>

        {/* Footer: Cancel and Save at bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCancel} variant="outlined" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              saving ||
              !layerRequiredComplete ||
              algorhythmIncompleteAndShown /* G layer: 7 Algorhythm fields required before Save */
            }
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AssetEditPage;
